import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Check, ArrowLeft, Phone, MessagesSquare, Users, Zap, HelpCircle } from 'lucide-react';
import { Breadcrumb } from './DemoSiteLayout';
import { getApiBaseUrl } from '../../../lib/apiConfig';
import { T, SURFACE, HAIRLINE, PrimaryButton } from './siteUI';

/* =========================================================
   Book a demo — the form behind every "Book a demo" button.

   Posts to /api/demo-requests on the agent API (Sales.js, port 3016), which
   saves the request so the team can follow up. The endpoint is public, so the
   form carries a honeypot field the server checks; keep it hidden and unlabelled.
   ========================================================= */


const PRODUCTS = [
  { value: 'conversational-ai', label: 'Conversational AI', sub: 'Voice, SMS, chat and email', icon: MessagesSquare },
  { value: 'teammate', label: 'Teammate', sub: 'AI employees', icon: Users },
  { value: 'orbiter', label: 'Orbiter', sub: 'AI infrastructure', icon: Zap },
  { value: 'not-sure', label: 'Not sure yet', sub: 'Help me work it out', icon: HelpCircle },
];

const TEAM_SIZES = ['1–10', '11–50', '51–200', '201–1,000', '1,000+'];

const field =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] outline-none transition-colors placeholder:text-slate-400 focus:border-teal-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-teal-600';

const Label = ({ htmlFor, children, optional }) => (
  <label htmlFor={htmlFor} className="mb-1.5 flex items-baseline gap-2 text-[12.5px] font-semibold text-slate-700">
    {children}
    {optional && <span className="text-[11px] font-normal text-slate-400">optional</span>}
  </label>
);

const DemoSiteBookDemo = () => {
  const location = useLocation();
  /* Where the visitor came from, so sales knows what they were reading. */
  const from = new URLSearchParams(location.search).get('from') || '';

  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    teamSize: '',
    product: from.includes('teammate')
      ? 'teammate'
      : from.includes('orbiter')
      ? 'orbiter'
      : from.includes('conversational')
      ? 'conversational-ai'
      : 'not-sure',
    message: '',
    website: '', // honeypot — must stay empty
  });
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (state === 'sending') return;
    setState('sending');
    setError('');
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/demo-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, sourcePath: from || location.pathname }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        setState('done');
      } else {
        setState('error');
        setError(data?.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setState('error');
      setError('Could not reach the server. Check your connection and try again.');
    }
  };

  if (state === 'done') {
    return (
      <>
        <Breadcrumb trail={['Home', 'Book a demo']} />
        <section className={`mx-auto mt-6 max-w-[620px] px-8 py-14 text-center ${SURFACE.band}`}>
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-teal-50 text-teal-600">
            <Check size={26} strokeWidth={2.4} />
          </span>
          <h1 className={`mt-5 ${T.h2}`}>Thanks — we&rsquo;ve got it.</h1>
          <p className={`mx-auto mt-3 max-w-[46ch] ${T.lede}`}>
            Someone from the team will email <span className="font-semibold text-slate-700">{form.email}</span> within
            one business day to set up your demo.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/site"
              className={`inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-[14px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 ${HAIRLINE} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600`}
            >
              <ArrowLeft size={15} /> Back to the site
            </Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Breadcrumb trail={['Home', 'Book a demo']} />

      <section className="grid grid-cols-1 items-start gap-10 py-8 lg:grid-cols-[1fr_1.1fr]">
        <div>
          <h1 className={T.display}>
            See it on <span className="text-teal-600">your own calls.</span>
          </h1>
          <p className={`mt-4 max-w-[46ch] ${T.lede}`}>
            Tell us a little about your operation and we&rsquo;ll set up a walkthrough against a workflow
            you actually run — not a canned script.
          </p>

          <ul className="mt-8 space-y-3.5">
            {[
              'A 30-minute call, screen shared',
              'Built around one of your real use cases',
              'Pricing for your actual volume',
              'No commitment, no card',
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 text-[14px] text-slate-700">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-teal-50 text-teal-600">
                  <Check size={13} strokeWidth={3} />
                </span>
                {line}
              </li>
            ))}
          </ul>

          <p className={`mt-8 border-t border-slate-200/70 pt-5 ${T.micro}`}>
            Prefer to talk now? Call{' '}
            <a href="tel:+18055000806" className="font-semibold text-teal-700 hover:text-teal-800">
              (805) 500-0806
            </a>
            .
          </p>
        </div>

        <form onSubmit={submit} className={`px-6 py-7 md:px-8 ${SURFACE.band}`} noValidate>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="name">Your name</Label>
              <input id="name" className={field} value={form.name} onChange={set('name')} required autoComplete="name" placeholder="Jordan Ellis" />
            </div>
            <div>
              <Label htmlFor="email">Work email</Label>
              <input id="email" type="email" className={field} value={form.email} onChange={set('email')} required autoComplete="email" placeholder="jordan@clinic.org" />
            </div>
            <div>
              <Label htmlFor="company" optional>Organization</Label>
              <input id="company" className={field} value={form.company} onChange={set('company')} autoComplete="organization" placeholder="Western Sierra Health" />
            </div>
            <div>
              <Label htmlFor="phone" optional>Phone</Label>
              <input id="phone" type="tel" className={field} value={form.phone} onChange={set('phone')} autoComplete="tel" placeholder="(805) 555-0142" />
            </div>
          </div>

          <fieldset className="mt-5">
            <legend className="mb-2 text-[12.5px] font-semibold text-slate-700">What are you interested in?</legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {PRODUCTS.map(({ value, label, sub, icon: Icon }) => {
                const on = form.product === value;
                return (
                  <label
                    key={value}
                    className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors ${
                      on ? 'border-teal-300 bg-teal-50/60' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="product"
                      value={value}
                      checked={on}
                      onChange={set('product')}
                      className="h-4 w-4 shrink-0 accent-teal-600"
                    />
                    <Icon size={16} strokeWidth={1.9} className={on ? 'text-teal-600' : 'text-slate-400'} />
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold leading-tight">{label}</span>
                      <span className={`block ${T.micro}`}>{sub}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <div className="mt-5">
            <Label htmlFor="teamSize" optional>Team size</Label>
            <select id="teamSize" className={field} value={form.teamSize} onChange={set('teamSize')}>
              <option value="">Select…</option>
              {TEAM_SIZES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="mt-5">
            <Label htmlFor="message" optional>What would you like to see?</Label>
            <textarea
              id="message"
              rows={3}
              className={`${field} resize-y`}
              value={form.message}
              onChange={set('message')}
              placeholder="We run about 4,000 outbound reminder calls a month and want to cut no-shows."
            />
          </div>

          {/* Honeypot. Hidden from people, irresistible to bots; the server
              discards any submission that fills it. */}
          <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
            <label htmlFor="website">Website</label>
            <input id="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
          </div>

          {state === 'error' && (
            <p role="alert" className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-[13px] text-red-700">
              {error}
            </p>
          )}

          <PrimaryButton type="submit" disabled={state === 'sending'} className="mt-6 w-full disabled:opacity-60">
            {state === 'sending' ? 'Sending…' : 'Request a demo'}
          </PrimaryButton>
          <p className={`mt-3 text-center ${T.micro}`}>
            We&rsquo;ll only use this to contact you about the demo.
          </p>
        </form>
      </section>
    </>
  );
};

export default DemoSiteBookDemo;
